import type { supabase as SupabaseInstance } from './supabase';

type Client = typeof SupabaseInstance;

export interface CurrentAdminDetails {
  id: string;
  full_name: string;
  email: string;
  /** full_name with email fallback, for denormalized *_name columns */
  display_name: string;
}

/**
 * Fetches the signed-in admin's identity for denormalization into
 * created_by_name / admin_name columns.
 *
 * Takes the client as a parameter (rather than importing it) so callers'
 * test mocks of the supabase module keep driving this path.
 */
export async function getCurrentAdminDetails(client: Client): Promise<CurrentAdminDetails> {
  const { data: adminUser } = await client.auth.getUser();

  if (!adminUser.user) {
    throw new Error('Your session has expired. Please log in again.');
  }

  const { data: admin, error: adminError } = await client
    .from('admin_users')
    .select('full_name, email')
    .eq('id', adminUser.user.id)
    .single();

  if (adminError) {
    throw new Error(`Failed to fetch admin details: ${adminError.message}`);
  }

  if (!admin) {
    throw new Error('Admin user details not found');
  }

  return {
    id: adminUser.user.id,
    full_name: admin.full_name,
    email: admin.email,
    display_name: admin.full_name || admin.email,
  };
}
